/**
 * Super Administrator — Employee activity detail (/super-admin/staff/{userId})
 *
 * Route name uses the `staff_` escape so this renders as a full page rather than nesting inside the
 * staff list route.
 *
 * Everything on this page is read-only. There is no action here that changes an employee's state —
 * monitoring answers "what happened", and mixing in account controls would make an audit surface
 * into an administration surface.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchStaffDetail,
  fetchStaffEvents,
  fetchStaffSessions,
  fetchStaffTimeline,
  type StaffTimelineEntry,
} from "@/lib/api/staff-monitoring";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { StaffActivityHeatMap } from "@/components/admin/StaffActivityHeatMap";
import { ColumnChart, RankedBarChart } from "@/components/analytics/charts";
import { PaginationControls } from "@/components/PaginationControls";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/loading";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  FileText,
  Globe,
  LogIn,
  LogOut,
  Monitor,
  Radio,
  RotateCcw,
  ShieldCheck,
  Timer,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/super-admin/staff_/$userId")({
  component: StaffDetailPage,
});

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function minutes(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value < 60) return `${Math.round(value)}m`;
  const h = Math.floor(value / 60);
  const m = Math.round(value % 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function seconds(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value < 60) return `${value}s`;
  return minutes(Math.round(value / 60));
}

function when(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

const PRESENCE_STYLE: Record<string, string> = {
  Online: "bg-green-500",
  Idle: "bg-amber-500",
  Offline: "bg-muted-foreground/40",
};

const TIMELINE_ICON: Record<StaffTimelineEntry["kind"], React.ReactNode> = {
  login: <LogIn className="h-3.5 w-3.5" />,
  logout: <LogOut className="h-3.5 w-3.5" />,
  "session-start": <Radio className="h-3.5 w-3.5" />,
  "session-end": <Radio className="h-3.5 w-3.5" />,
  "token-expired": <Timer className="h-3.5 w-3.5" />,
  page: <Globe className="h-3.5 w-3.5" />,
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5 min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium break-words">{value ?? "—"}</dd>
    </div>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function StaffDetailPage() {
  const { userId } = Route.useParams();
  const { isSuperAdmin, loading } = useAuth();

  const [sessionPage, setSessionPage] = useState(1);
  const [sessionPageSize, setSessionPageSize] = useState(10);
  const [eventPage, setEventPage] = useState(1);
  const [eventPageSize, setEventPageSize] = useState(25);
  const [timelineDay, setTimelineDay] = useState(() => new Date().toISOString().slice(0, 10));

  const detail = useQuery({
    queryKey: ["staff-monitoring", "detail", userId],
    queryFn: () => fetchStaffDetail(userId),
    enabled: isSuperAdmin,
    refetchInterval: 60_000,
  });

  const sessions = useQuery({
    queryKey: ["staff-monitoring", "sessions", userId, sessionPage, sessionPageSize],
    queryFn: () =>
      fetchStaffSessions(userId, { pageNumber: sessionPage, pageSize: sessionPageSize }),
    enabled: isSuperAdmin,
  });

  const events = useQuery({
    queryKey: ["staff-monitoring", "events", userId, eventPage, eventPageSize],
    queryFn: () => fetchStaffEvents(userId, { pageNumber: eventPage, pageSize: eventPageSize }),
    enabled: isSuperAdmin,
  });

  const timeline = useQuery({
    queryKey: ["staff-monitoring", "timeline", userId, timelineDay],
    queryFn: () => fetchStaffTimeline(userId, new Date(`${timelineDay}T00:00:00Z`).toISOString()),
    enabled: isSuperAdmin,
  });

  const d = detail.data;

  const hourData = useMemo(
    () =>
      (d?.activeSecondsByHour ?? []).map((secs, hour) => ({
        label: String(hour).padStart(2, "0"),
        value: Math.round(secs / 60),
        fullLabel: `${String(hour).padStart(2, "0")}:00 – ${String((hour + 1) % 24).padStart(2, "0")}:00`,
      })),
    [d],
  );

  const dayData = useMemo(
    () =>
      (d?.activeSecondsByDayOfWeek ?? []).map((secs, index) => ({
        label: DAY_SHORT[index] ?? String(index),
        value: Math.round(secs / 60),
        fullLabel: DAY_NAMES[index] ?? String(index),
      })),
    [d],
  );

  const pageData = useMemo(
    () =>
      (d?.mostUsedPages ?? []).map((page) => ({
        label: page.path,
        value: page.visits,
      })),
    [d],
  );

  if (loading) return null;
  if (!isSuperAdmin) {
    return (
      <AccessDenied message="Employee activity detail is limited to the Super Administrator." />
    );
  }

  if (detail.isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-[1500px] mx-auto space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.isError || !d) {
    return (
      <div className="p-6 lg:p-8 max-w-2xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {(detail.error as Error)?.message ??
                "This employee could not be found. Monitoring covers staff accounts only — students are never tracked."}
            </p>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => detail.refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/super-admin/staff">Back to staff monitor</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1500px] mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm" className="gap-1.5 -ml-2">
          <Link to="/super-admin/staff">
            <ArrowLeft className="h-4 w-4" /> Staff monitor
          </Link>
        </Button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold tracking-tight truncate">
                {d.fullName || d.email}
              </h1>
              <span className="inline-flex items-center gap-2 text-sm">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${PRESENCE_STYLE[d.presence] ?? PRESENCE_STYLE.Offline} ${
                    d.presence === "Online" ? "animate-pulse" : ""
                  }`}
                />
                {d.presenceDetail}
              </span>
              {!d.isActive && <Badge variant="destructive">Account disabled</Badge>}
            </div>
            <p className="text-muted-foreground text-sm mt-1">{d.email}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {d.roles.map((r) => (
                <Badge key={r} variant="secondary" className="text-xs">
                  {r}
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:min-w-[520px]">
            <StatTile label="Active today" value={minutes(d.activity.todayMinutes)} />
            <StatTile label="This week" value={minutes(d.activity.thisWeekMinutes)} />
            <StatTile label="This month" value={minutes(d.activity.thisMonthMinutes)} />
            <StatTile
              label="Days since login"
              value={d.daysSinceLastLogin < 0 ? "never" : String(d.daysSinceLastLogin)}
            />
          </div>
        </div>
      </div>

      {/* Current session + profile */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="h-4 w-4 text-primary" /> Current session
            </CardTitle>
            <CardDescription className="text-xs">
              Live wall-clock and accrued-activity figures are tracked separately — a session left
              open in a background tab adds wall-clock but no activity.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {d.currentSession ? (
              <dl className="grid gap-4 sm:grid-cols-3">
                <Field label="Started" value={when(d.currentSession.startedAt)} />
                <Field label="Session length" value={minutes(d.currentSession.durationMinutes)} />
                <Field label="Active in session" value={minutes(d.currentSession.activeMinutes)} />
                <Field
                  label="Current page"
                  value={
                    d.currentPage ? (
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                        {d.currentPage}
                      </code>
                    ) : (
                      "—"
                    )
                  }
                />
                <Field label="On this page for" value={seconds(d.currentPageSeconds)} />
                <Field
                  label="Device"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                      {[
                        d.currentSession.browser,
                        d.currentSession.operatingSystem,
                        d.currentSession.deviceType,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "unknown"}
                    </span>
                  }
                />
              </dl>
            ) : (
              <div className="py-6 text-center space-y-1">
                <p className="text-sm text-muted-foreground">No open session.</p>
                <p className="text-xs text-muted-foreground/70">
                  Last seen {when(d.lastSeenAt)} · last login {when(d.lastLoginAt)} · last logout{" "}
                  {when(d.lastLogoutAt)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" /> Role &amp; permissions
            </CardTitle>
            <CardDescription className="text-xs">
              Derived from assigned roles — descriptive, not the authorization source.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <dl className="grid grid-cols-2 gap-3">
              <Field label="Job title" value={d.jobTitle} />
              <Field label="Department" value={d.department} />
            </dl>
            <Separator />
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
              {d.permissions.length === 0 ? (
                <p className="text-xs text-muted-foreground">No permissions mapped.</p>
              ) : (
                d.permissions.map((p) => (
                  <Badge key={p} variant="outline" className="text-[10px] font-normal">
                    {p}
                  </Badge>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Averages */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" /> Averages
          </CardTitle>
          <CardDescription className="text-xs">
            Active time excludes periods where the browser stopped reporting, so a crashed tab or a
            closed laptop does not inflate these figures.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <StatTile label="Avg / day" value={minutes(d.averageDailyActiveMinutes)} />
            <StatTile label="Avg / week" value={minutes(d.averageWeeklyActiveMinutes)} />
            <StatTile label="Avg / month" value={minutes(d.averageMonthlyActiveMinutes)} />
            <StatTile label="Avg session" value={minutes(d.averageSessionMinutes)} />
            <StatTile label="Total sessions" value={String(d.totalSessions)} />
            <StatTile label="Yesterday" value={minutes(d.activity.yesterdayMinutes)} />
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Most active hours</CardTitle>
            <CardDescription className="text-xs">
              Active minutes by hour of day (UTC). Peak: {String(d.mostActiveHour).padStart(2, "0")}
              :00
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ColumnChart
              data={hourData}
              seriesLabel="Active minutes"
              valueFormatter={(v) => minutes(v)}
              highlightIndex={d.mostActiveHour}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Most active days</CardTitle>
            <CardDescription className="text-xs">
              Active minutes by day of week. Peak: {DAY_NAMES[d.mostActiveDayOfWeek] ?? "—"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ColumnChart
              data={dayData}
              seriesLabel="Active minutes"
              valueFormatter={(v) => minutes(v)}
              highlightIndex={d.mostActiveDayOfWeek}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Most used pages</CardTitle>
            <CardDescription className="text-xs">
              Paths are normalised — record ids collapse to <code>:id</code> so visits to the same
              screen group together.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pageData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No page visits recorded.
              </p>
            ) : (
              <RankedBarChart data={pageData} seriesLabel="Visits" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Page time</CardTitle>
            <CardDescription className="text-xs">Total time spent per page.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {d.mostUsedPages.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Nothing recorded yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2 w-8">#</th>
                    <th className="text-left font-semibold px-4 py-2">Page</th>
                    <th className="text-right font-semibold px-4 py-2">Visits</th>
                    <th className="text-right font-semibold px-4 py-2">Total time</th>
                    <th className="text-right font-semibold px-4 py-2">Avg</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {d.mostUsedPages.map((page, i) => (
                    <tr key={page.path} className="hover:bg-muted/30">
                      <td className="px-4 py-2 text-muted-foreground tabular-nums">{i + 1}</td>
                      <td className="px-4 py-2">
                        <code className="text-xs">{page.path}</code>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{page.visits}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {seconds(page.totalSeconds)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                        {page.visits > 0
                          ? seconds(Math.round(page.totalSeconds / page.visits))
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Heat map */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" /> Activity heat map
          </CardTitle>
          <CardDescription className="text-xs">
            Accrued active minutes per hour, shaded relative to this employee's own busiest hour.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StaffActivityHeatMap cells={d.heatMap} />
        </CardContent>
      </Card>

      {/* Logs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" /> Logs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="sessions">
            <TabsList>
              <TabsTrigger value="sessions">Sessions</TabsTrigger>
              <TabsTrigger value="events">Login / logout events</TabsTrigger>
              <TabsTrigger value="timeline">Page timeline</TabsTrigger>
            </TabsList>

            {/* Sessions */}
            <TabsContent value="sessions" className="mt-4 space-y-3">
              {sessions.isLoading ? (
                <TableSkeleton rows={5} columns={6} />
              ) : sessions.isError ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {(sessions.error as Error)?.message ?? "Could not load sessions."}
                </p>
              ) : (sessions.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No sessions recorded.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="text-left font-semibold px-4 py-2 w-10">#</th>
                          <th className="text-left font-semibold px-4 py-2">Started</th>
                          <th className="text-left font-semibold px-4 py-2">Ended</th>
                          <th className="text-right font-semibold px-4 py-2">Duration</th>
                          <th className="text-right font-semibold px-4 py-2">Active</th>
                          <th className="text-left font-semibold px-4 py-2">Ended by</th>
                          <th className="text-left font-semibold px-4 py-2">Device</th>
                          <th className="text-left font-semibold px-4 py-2">IP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sessions.data!.items.map((s, i) => (
                          <tr key={s.id} className="hover:bg-muted/30">
                            <td className="px-4 py-2 text-muted-foreground tabular-nums">
                              {(sessions.data!.pageNumber - 1) * sessions.data!.pageSize + i + 1}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">{when(s.startedAt)}</td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {s.isOpen ? (
                                <Badge variant="secondary" className="text-[10px]">
                                  open
                                </Badge>
                              ) : (
                                when(s.endedAt)
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {minutes(s.durationMinutes)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums font-medium">
                              {minutes(s.activeMinutes)}
                            </td>
                            <td className="px-4 py-2 text-xs text-muted-foreground">
                              {s.endReasonLabel}
                            </td>
                            <td className="px-4 py-2 text-xs text-muted-foreground">
                              {[s.browser, s.operatingSystem].filter(Boolean).join(" · ") || "—"}
                            </td>
                            <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                              {s.ipAddress || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationControls
                    pageNumber={sessions.data!.pageNumber}
                    pageSize={sessions.data!.pageSize}
                    totalPages={Math.max(1, sessions.data!.totalPages)}
                    totalCount={sessions.data!.totalCount}
                    hasNextPage={sessions.data!.hasNextPage}
                    hasPreviousPage={sessions.data!.hasPreviousPage}
                    onPageChange={setSessionPage}
                    onPageSizeChange={(size) => {
                      setSessionPageSize(size);
                      setSessionPage(1);
                    }}
                    isLoading={sessions.isFetching}
                  />
                </>
              )}
            </TabsContent>

            {/* Events */}
            <TabsContent value="events" className="mt-4 space-y-3">
              {events.isLoading ? (
                <TableSkeleton rows={5} columns={4} />
              ) : events.isError ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {(events.error as Error)?.message ?? "Could not load the event log."}
                </p>
              ) : (events.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No events recorded.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="text-left font-semibold px-4 py-2 w-10">#</th>
                          <th className="text-left font-semibold px-4 py-2">Event</th>
                          <th className="text-left font-semibold px-4 py-2">When</th>
                          <th className="text-left font-semibold px-4 py-2">Page</th>
                          <th className="text-left font-semibold px-4 py-2">IP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {events.data!.items.map((e, i) => (
                          <tr key={e.id} className="hover:bg-muted/30">
                            <td className="px-4 py-2 text-muted-foreground tabular-nums">
                              {(events.data!.pageNumber - 1) * events.data!.pageSize + i + 1}
                            </td>
                            <td className="px-4 py-2 font-medium">{e.eventTypeLabel}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{when(e.occurredAt)}</td>
                            <td className="px-4 py-2 text-xs text-muted-foreground">
                              {e.page ? <code>{e.page}</code> : "—"}
                            </td>
                            <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                              {e.ipAddress || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationControls
                    pageNumber={events.data!.pageNumber}
                    pageSize={events.data!.pageSize}
                    totalPages={Math.max(1, events.data!.totalPages)}
                    totalCount={events.data!.totalCount}
                    hasNextPage={events.data!.hasNextPage}
                    hasPreviousPage={events.data!.hasPreviousPage}
                    onPageChange={setEventPage}
                    onPageSizeChange={(size) => {
                      setEventPageSize(size);
                      setEventPage(1);
                    }}
                    isLoading={events.isFetching}
                  />
                </>
              )}
            </TabsContent>

            {/* Timeline */}
            <TabsContent value="timeline" className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground" htmlFor="timeline-day">
                  Day
                </label>
                <input
                  id="timeline-day"
                  type="date"
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={timelineDay}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setTimelineDay(e.target.value)}
                />
              </div>

              {timeline.isLoading ? (
                <TableSkeleton rows={6} columns={3} />
              ) : timeline.isError ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {(timeline.error as Error)?.message ?? "Could not load the timeline."}
                </p>
              ) : (timeline.data?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No activity on this day.
                </p>
              ) : (
                <ol className="relative border-l ml-3 space-y-4 py-2">
                  {timeline.data!.map((entry, i) => (
                    <li key={`${entry.atUtc}-${i}`} className="ml-6">
                      <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary ring-4 ring-background">
                        {TIMELINE_ICON[entry.kind]}
                      </span>
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <time className="text-xs text-muted-foreground tabular-nums">
                          {new Date(entry.atUtc).toLocaleTimeString()}
                        </time>
                        <p className="text-sm font-medium">{entry.label}</p>
                        {entry.durationSeconds != null && (
                          <span className="text-xs text-muted-foreground">
                            ({seconds(entry.durationSeconds)})
                          </span>
                        )}
                      </div>
                      {entry.page && (
                        <code className="text-xs text-muted-foreground">{entry.page}</code>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
